import { z } from 'zod'

/** IVL 재고 응답 계약. 기존 조회 응답과 별도로 검증한다. */
const quantity = z.number().int().safe()
const count = quantity.nonnegative()
const text = z.string().nullable()
const price = z.number().finite().nullable()

const itemSchema = z.object({
  sprNm: z.string(),
  shipperName: text,
  serviceType: text,
  brdNm: text,
  stlNo: text,
  gdsCd: text,
  // Spec: docs/spec/inventory/SPEC-inventory-ivl-sku-id-A.yaml#ivl-row-sku-id-is-string-or-null
  skuId: text,
  barcode: text,
  gdsNm: text,
  gdsType: text,
  gdsOpt: text,
  whNm: text,
  totalQty: quantity,
  availQty: quantity,
  allocQty: quantity,
  remainQty: quantity,
  gdsColor: text,
  gdsGroup1: text,
  payablePrice: price,
  receivablePrice: price,
  salesAmount: price,
})

const failureSchema = z.object({
  targetType: z.enum(['AGGREGATE_ROW', 'SOURCE_VIU']),
  code: z.string(),
  reason: z.string(),
  errorRef: z.string(),
  sourceViuCount: count,
  sourceViusTruncated: z.boolean(),
  sourceVius: z.array(z.object({
    stockViuId: count,
    skuId: z.string(),
    namespace: z.string(),
    rootNodeId: z.string(),
    leafNodeId: z.string(),
    batchPath: z.string(),
    batchId: z.string(),
  })),
})

const supplementSchema = z.object({
  status: z.enum(['SUCCESS', 'PARTIAL', 'FAILED', 'SKIPPED']),
  code: text,
  failures: z.array(z.object({ itemIndex: count, code: z.string() })),
})

export const ivlInventoryResultSchema = z.object({
  items: z.array(itemSchema),
  failures: z.array(failureSchema),
  pageInfo: z.object({
    page: count,
    size: count.positive().max(10_000),
    totalCount: count.nullish(),
    totalPages: count.nullish(),
    // FE-first 추가 계약: null/누락은 미지원, false는 다음 페이지 없음이다.
    hasMore: z.boolean().nullish(),
    // true면 totalCount는 잘린 하한, false면 정확한 값. null/누락은 정확도 미제공.
    // 원본 totalCount/totalPages를 보정하거나 표시용 1000으로 치환하지 않는다.
    countCapped: z.boolean().nullish(),
    // true: 기존 엑셀 경로가 >=30,000건을 서버에서 차단한다. 운영 flag/요청 옵션이 아니다.
    exportLimitEnforced: z.boolean().nullish(),
  }).refine((page) => typeof page.hasMore === 'boolean' || (page.totalCount != null && page.totalPages != null),
    '기존 페이지 응답에는 전체 건수와 페이지 수가 필요해요.'),
  summary: z.object({
    // 전역 검증 미수행은 null이다. 0건/완전한 재고로 기본값을 채우지 않는다.
    complete: z.boolean().nullable(),
    failureCount: count,
    unresolvedSourceCount: count.nullable(),
    errorRef: text.optional(),
    quantityBasis: z.string().optional(),
  }),
  supplement: z.object({ productAttributes: supplementSchema, prices: supplementSchema }),
}).refine((result) => (
  result.items.length + result.failures.length <= result.pageInfo.size
  && Object.values(result.supplement).every((outcome) => outcome.failures.every(({ itemIndex }) => itemIndex < result.items.length))
), '재고 조회 응답의 페이지 또는 실패 행 정보가 올바르지 않아요.')

export type IvlInventoryResult = z.infer<typeof ivlInventoryResultSchema>
export type IvlInventoryItem = IvlInventoryResult['items'][number]
export type InventorySupplementOutcome = IvlInventoryResult['supplement']['prices']

export interface IvlInventoryFilters {
  /** 화주명 검색이다. 서버가 응답하는 sprNm 화주코드와 구분한다. */
  sprNm?: string[]
  logisticsServiceTypes?: string[]
  warehouseCode?: string
  barcode?: string[]
  skuId?: string[]
  brdNm?: string
  gdsCd?: string
  stlNo?: string
  stcBarcode?: string[]
  stcBrdNm?: string[]
  stcStlNo?: string[]
  includeZeroQuantity?: boolean
}

export interface IvlInventoryRequest extends IvlInventoryFilters {
  page: number
  size: number
}

export interface IvlInventoryExportRequest extends IvlInventoryFilters {
  viewerShipperCode: string
  reasonCode: string
  reasonDetail?: string
}

export interface InventoryWarehouse {
  warehouseCode: string
  warehouseName: string
  status: boolean
}
